import type { NamedEnv } from '@mathflat/shared/@common/devToolbar'
import { mountDevToolbar } from '@mathflat/shared/@common/devToolbar'
import { jwtTokenService } from '@mathflat/shared/@modules/auth/jwtTokenService'
import { Observer, observer } from 'mobx-react'
import { Suspense, useEffect } from 'react'
import { isDesktop } from 'react-device-detect'
import { Outlet, useNavigate } from 'react-router-dom'

import { routeName } from '~/@common/constants'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { useOrientation } from '~/@common/hooks/useOrientation'
import { useTransferDoneRedirect } from '~/@common/integration/useTransferDoneRedirect'
import { showPendingToastIfAny } from '~/@common/services/forceUpdate/postUpdateToast'
import { handwrittenNoteTooltipService } from '~/@common/services/HandwrittenTooltip.service'
import { nativeEventBus } from '~/@common/services/nativeEventBus'
import { remoteStorageService } from '~/@common/services/remoteStorage.service'
import { commonRepo } from '~/@common/services/repo.service'
import { VirtualKeyboardDrawer } from '~/@common/ui/(Keypad)'
import { virtualKeyboardService } from '~/@common/ui/(Keypad)/keypad.service'
import Modal from '~/@common/ui/modal/Modal'
import { PortalRootLayoutHeader } from '~/@common/ui/PortalRootLayoutHeader'
import SwiperProvider from '~/@common/ui/SwiperController/Swiper'
import { envUtils } from '~/@common/utils/envUtils'
import { GNB } from '~/@pages/@widgets/(Navigation)/GNB'
import SideGNB from '~/@pages/@widgets/(Navigation)/SideGNB/SideGNB'
import Main from '~/@pages/@widgets/Main'
import ScreenSizeGuide from '~/@pages/@widgets/ScreenSizeGuide'

import useApp from './hooks/useApp'
import { useDataTracking } from './hooks/useMixpanel'
import { initAxiosDefaultBaseURL } from './init/envSetting/envSettings'
import { openB2GSignupPopup } from './init/popup'
import { RouteLoadingFallback } from './RouteLoadingFallback'

const App = () => {
  const { location } = useApp()
  const navigate = useNavigate()
  const { type } = useOrientation()
  const { isMobile } = useStudentAppMediaQuery()

  const desktopSmallScreenWhitelist: string[] = [
    routeName.login,
    routeName.loginCreverse,
    routeName.loginCreverseBridge,
    routeName.profile,
  ]
  const isShowSmallScreenGuide =
    isDesktop && envUtils.isLive && !desktopSmallScreenWhitelist.includes(location.pathname)

  const { isParent } = commonRepo.permission
  const isSideGNBLayoutMode = !isParent && !isMobile && commonRepo.isElementarySchoolflat
  const isStagingOrLive = envUtils.isLive || envUtils.isStaging
  // e2e 가 띄운 dev 서버에서는 띄우지 않는다 — 떠 있는 알약은 어디에 두어도 어떤 화면의 컨트롤을 덮어 클릭을 가로챈다.
  const isE2eDevServer = Boolean(import.meta.env.VITE_E2E_DEV_SERVER)

  useEffect(() => {
    if (isStagingOrLive || isE2eDevServer) return
    const handle = mountDevToolbar({
      onApiEnvApply: (url: string) => {
        jwtTokenService.clearTokens()
        if (url === 'live' || url === 'stg' || url === 'dev') {
          jwtTokenService.setEnvironment(url)
        } else {
          jwtTokenService.setEnvironment('custom', url)
        }
        initAxiosDefaultBaseURL(url)
        navigate(routeName.login)
      },
      onSecondaryEnvApply: (_env: NamedEnv, customUrl: string) => {
        jwtTokenService.clearTokens()
        initAxiosDefaultBaseURL(customUrl)
        navigate(routeName.login)
      },
    })
    return () => handle.unmount()
  }, [])

  useDataTracking()
  // ★ 통합 마이그레이션 BroadcastChannel — student-up 탭이 sender 마이그레이션 완료 알림 받으면 다른 탭도 student.* 으로 즉시 수렴
  useTransferDoneRedirect()

  useEffect(() => {
    showPendingToastIfAny()
  }, [])

  useEffect(() => {
    if (type === 'portrait') {
      handwrittenNoteTooltipService.showHandwrittenInfoTooltip()
    }
  }, [type])

  useEffect(() => {
    const B2G_SIGNUP_MODAL_SIGNUP_FROM = ['WHALE_SPACE', 'NAVER_WORKS']

    remoteStorageService.get('isCompletedInitialStudentInfoSetting').then((remoteStorage) => {
      if (
        commonRepo.signUpFrom &&
        B2G_SIGNUP_MODAL_SIGNUP_FROM.includes(commonRepo.signUpFrom) &&
        !remoteStorage?.isCompletedInitialStudentInfoSetting &&
        location.pathname !== routeName.login
      ) {
        openB2GSignupPopup()
      }
    })
  }, [location.pathname])

  // ⚠️ 이 useEffect는 반드시 App에서 가장 마지막 useEffect여야 한다.
  // React는 useEffect를 등록 순서대로 실행하므로, ready()가 먼저 실행되면
  // 아직 구독 등록이 끝나지 않은 상태에서 네이티브에게 "준비 완료" 신호가 전송된다.
  // 위치를 옮길 경우 registerDevice·navigateTo 등의 이벤트가 유실될 수 있다.
  useEffect(() => {
    nativeEventBus.ready()
  }, [])

  return (
    <>
      {/* {!envUtils.isLive && <IntegrationDevPanel />} */}
      {isShowSmallScreenGuide && <ScreenSizeGuide />}
      {location.pathname !== routeName.serverMaintenance && (
        <PortalRootLayoutHeader isSideHeaderLayoutMode={isSideGNBLayoutMode}>
          {isSideGNBLayoutMode ? <SideGNB /> : <GNB />}
        </PortalRootLayoutHeader>
      )}
      <SwiperProvider>
        <Main>
          <Suspense fallback={<RouteLoadingFallback />}>
            <Outlet />
          </Suspense>
        </Main>
      </SwiperProvider>
      <Modal.Container />
      {isMobile && (
        <Observer>
          {() => (
            <VirtualKeyboardDrawer
              open={virtualKeyboardService.show && virtualKeyboardService.showType === 'drawer'}
              keypadType={virtualKeyboardService.type}
              closeKeyboard={() => virtualKeyboardService.close()}
              onKeyButtonClick={(e, latexNodeType) => {
                virtualKeyboardService.onKeyButtonClick(e, latexNodeType)
              }}
            />
          )}
        </Observer>
      )}
    </>
  )
}

export default observer(App)
